import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import ordersService, {
  type ChangeAllChangeSet,
  type ChangeAllPreviewRow,
  type ChangeAllApplyResult,
} from '../../../services/orders.service';

interface Props {
  isOpen: boolean;
  /** The REST of the target set (the anchor pro has already been saved). */
  pros: (number | string)[];
  /** The flagged-field changes captured from the anchor's edits. Optional now:
   *  used by the default preview/apply fns and the default summary chips. */
  changes?: ChangeAllChangeSet;
  onClose: () => void;
  /** Called after an apply run so the grid / anchor can refetch. */
  onApplied?: () => void;
  /** Injected preview; defaults to changeAllPreview({ pros, changes }). */
  previewFn?: (pros: (number | string)[], signal?: AbortSignal) => Promise<{ rows: ChangeAllPreviewRow[] }>;
  /** Injected apply for ONE chunk; defaults to changeAllApply({ pros: chunk, changes, submitNonce }). */
  applyFn?: (prosChunk: (number | string)[], submitNonce: string) => Promise<{ results: ChangeAllApplyResult[] }>;
  /** Header/summary node; defaults to the changes.order/invoice/legs column chips. */
  summary?: React.ReactNode;
  /** Extra summary rendered BELOW the field chips (shown in addition to them) — e.g.
   *  the invoice line items also being fanned out in the same Apply. */
  extraSummary?: React.ReactNode;
  /** Optional dialog title override (default 'Change All'). */
  title?: string;
  /** What one record is called, singular and lowercase (default 'pro'). The Templates
   *  board fans TEMPLATES through this same modal, and "Apply to 12 pros" is wrong
   *  there in a way that makes the user doubt what they are about to change. */
  recordNoun?: string;
  /** The anchor record, when this fan-out has one. Purely explanatory: users
   *  read "21 in set" on the banner and "20" here and reasonably concluded one
   *  was being missed. Naming the anchor and the total makes the arithmetic
   *  visible instead of leaving them to work it out.
   *
   *  The anchor stays OUT of the fan deliberately — it may carry edits that
   *  should not propagate, and in production it still saves through cmsapi while
   *  this path is native. So the fix is to explain the split, not close it. */
  anchorPro?: number | string | null;
}

/**
 * Change All — review + results modal.
 *
 * The bulk fan-out of the /pro "Change All" flow. The anchor pro was already
 * saved normally; this modal applies the fields the user flagged "apply to all"
 * across the rest of the target set. Two steps:
 *   1. review  — per-pro eligibility summary (X will change / Y can't, with
 *                reasons) + the compact list of fields being applied.
 *   2. results — the applied / skipped breakdown returned by the apply run.
 *
 * Adapted from ChangeTemplateBulkModal (its review + results halves), minus the
 * template search step — the field changes are passed in, not picked here. The
 * backend re-validates every pro and applies each in its own transaction, so an
 * ineligible or failing pro is skipped (with a reason) without aborting the batch.
 */
const ChangeAllReviewModal: React.FC<Props> = ({
  isOpen,
  pros,
  changes,
  onClose,
  onApplied,
  previewFn,
  applyFn,
  summary,
  extraSummary,
  title = 'Change All',
  recordNoun = 'pro',
  anchorPro = null,
}) => {
  const [step, setStep] = useState<'review' | 'results'>('review');
  // "1 pro" / "12 pros" / "12 templates". Naive 's' pluralization is right for both
  // nouns this modal serves; anything irregular would need its own plural prop.
  const plural = (n: number) => `${recordNoun}${n === 1 ? '' : 's'}`;

  // Effective preview/apply fns. Default to the Phase-1 ChangeSet fan-out; a
  // caller can inject different behavior (e.g. a notes append to another
  // endpoint) while reusing the whole review/apply/results shell.
  const doPreview =
    previewFn ??
    ((p: (number | string)[], signal?: AbortSignal) =>
      ordersService.changeAllPreview({ pros: p, changes: changes ?? {} }, signal));
  const doApply =
    applyFn ??
    ((chunk: (number | string)[], nonce: string) =>
      ordersService.changeAllApply({ pros: chunk, changes: changes ?? {}, submitNonce: nonce }));

  const [rows, setRows] = useState<ChangeAllPreviewRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [results, setResults] = useState<ChangeAllApplyResult[] | null>(null);
  // Live progress across the chunked apply (done/total pros) for the progress bar.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Synchronous re-entrancy guard (a double-click must not fire two apply runs).
  const applyingRef = useRef(false);

  // The modal stays mounted across open/close (the parent toggles isOpen), so its
  // state must be reset on close — otherwise reopening for a NEW set of pros would
  // show the PREVIOUS run's results. Reset when closed so the next open starts
  // fresh at the review step.
  useEffect(() => {
    if (isOpen) return;
    setStep('review');
    setRows(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setResults(null);
    setApplyError(null);
    setApplying(false);
    setProgress(null);
    applyingRef.current = false;
  }, [isOpen]);

  // On open, preview all target pros against the flagged changes. The backend
  // re-validates each pro and returns per-pro eligibility + reasons.
  useEffect(() => {
    if (!isOpen || step !== 'review') return;
    const ctrl = new AbortController();
    setPreviewLoading(true);
    setPreviewError(null);
    setRows(null);
    doPreview(pros, ctrl.signal)
      .then((res) => setRows(res.rows))
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setPreviewError(detail || 'Could not check the target pros.');
        setRows([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setPreviewLoading(false);
      });
    return () => ctrl.abort();
  }, [isOpen, step, pros, changes]);

  const eligible = useMemo(() => (rows ?? []).filter((r) => r.eligible), [rows]);
  const skipped = useMemo(() => (rows ?? []).filter((r) => !r.eligible), [rows]);
  // Eligible pros carrying non-blocking caveats (e.g. a mismatched invoice line skipped).
  const warned = useMemo(
    () => (rows ?? []).filter((r) => r.eligible && (r.warnings?.length ?? 0) > 0),
    [rows],
  );

  // Compact list of the field columns being applied to every changed pro —
  // order + invoice + per-leg columns from the default ChangeSet.
  const fieldColumns = useMemo(
    () => [
      ...(changes?.order ?? []).map((c) => c.column),
      ...(changes?.invoice ?? []).map((c) => c.column),
      ...(changes?.legs ?? []).map((c) => c.column),
    ],
    [changes],
  );

  // Apply in small chunks so the modal can show live progress (X of N). Only the
  // preview-eligible pros are sent (ineligible ones were already shown as skipped).
  const APPLY_CHUNK = 4;

  const handleApply = async () => {
    if (applyingRef.current || eligible.length === 0) return;
    applyingRef.current = true;
    setApplying(true);
    setApplyError(null);
    const eligiblePros = eligible.map((r) => String(r.pronumber));
    const total = eligiblePros.length;
    setProgress({ done: 0, total });
    // One nonce for the whole run — the (nonce, pronumber) idempotency key makes it
    // safe across chunks (each pro is in exactly one chunk).
    const nonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `ca-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const acc: ChangeAllApplyResult[] = [];
    let errored: string | null = null;
    for (let i = 0; i < total; i += APPLY_CHUNK) {
      const chunk = eligiblePros.slice(i, i + APPLY_CHUNK);
      try {
        const res = await doApply(chunk, nonce);
        acc.push(...(res.results || []));
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        errored = detail || 'Could not apply the changes.';
        break;
      }
      setProgress({ done: Math.min(i + APPLY_CHUNK, total), total });
    }

    applyingRef.current = false;
    setApplying(false);
    setProgress(null);
    // Nothing applied + errored -> stay on review with the error. Otherwise show
    // the results (noting a partial error if the run stopped early).
    if (errored && acc.length === 0) {
      setApplyError(errored);
      return;
    }
    if (errored) setApplyError(errored);
    setResults(acc);
    setStep('results');
    onApplied?.();
  };

  // Alt+C closes (never mid-apply). Apply gets no chord: it writes the change to
  // every target PRO, and the review step exists to slow that down.
  useAltHotkeys({ C: applying ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const handleClose = () => {
    if (applyingRef.current) return;
    onClose();
  };

  const applied = (results ?? []).filter((r) => r.applied);
  const notApplied = (results ?? []).filter((r) => !r.applied);
  // Applied, but something attached to the change was skipped — surfaced so a partial
  // outcome isn't reported as a clean one.
  const appliedWithCaveats = applied.filter((r) => (r.warnings?.length ?? 0) > 0);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/40 p-4"
      onMouseDown={handleClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[min(880px,96vw)] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={
            // The header carries the number the user came in expecting — the
            // banner's set size, anchor included. The body below is what says
            // how many are actually being fanned, and why that is one fewer.
            anchorPro != null
              ? `${title} — ${pros.length + 1} ${plural(pros.length + 1)} total`
              : `${title} — ${pros.length} ${plural(pros.length)}`
          }
          onClose={applying ? undefined : handleClose}
          dragHandleProps={dragHandleProps}
          className="shrink-0"
        />

        {step === 'review' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="shrink-0">
              <div className="text-sm font-semibold text-slate-800 dark:text-gray-100">
                Apply your changes to {pros.length} other {plural(pros.length)}
              </div>
              {anchorPro != null && (
                <div className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                  Anchor {recordNoun} {anchorPro} — already changed, so it is not
                  listed below.
                </div>
              )}
            </div>

            {/* Summary — injected node, else the default field-column chips. */}
            {summary ?? (
              fieldColumns.length > 0 && (
                <div className="shrink-0">
                  <div className="text-xs font-medium text-slate-600 dark:text-gray-300 mb-1">
                    Fields to apply (to every changed {recordNoun}):
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {fieldColumns.map((col, i) => (
                      <span
                        key={`${col}-${i}`}
                        className="inline-flex items-center rounded bg-slate-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-slate-700 dark:text-gray-200"
                      >
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* The per-diem note travels with a perdiemapproval change and writes a
                pro note + history row on every target, so say so before Apply. */}
            {changes?.perDiemNotes !== undefined && (
              <div className="shrink-0 rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-slate-700 dark:text-gray-200">
                <span className="font-medium">Per diem responsibility note</span> — each
                pro also gets{' '}
                {changes.perDiemNotes.trim() === '' ? (
                  <>its note <span className="font-medium">cleared</span>.</>
                ) : (
                  <>
                    a “PER DIEM APPROVAL” note reading “{changes.perDiemNotes.trim()}”.
                  </>
                )}
              </div>
            )}

            {/* Extra summary (e.g. invoice line items fanned in the same Apply). */}
            {extraSummary}

            {previewError ? (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                {previewError}
              </div>
            ) : previewLoading ? (
              <div className="py-10 text-center text-sm text-slate-500 dark:text-gray-400">
                Checking {pros.length} {plural(pros.length)}…
              </div>
            ) : (
              <>
                {/* Eligibility summary. With an anchor there are THREE boxes, and
                    they add up to the set size in the header — the anchor is
                    accounted for rather than left to be inferred from a gap. */}
                <div
                  className={
                    'grid gap-3 shrink-0 '
                    + (anchorPro != null ? 'grid-cols-3' : 'grid-cols-2')
                  }
                >
                  {anchorPro != null && (
                    <div
                      className="rounded border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2"
                      title={`Anchor ${recordNoun} ${anchorPro}`}
                    >
                      <div className="text-lg font-semibold text-blue-700 dark:text-blue-300">
                        1
                      </div>
                      <div className="text-xs text-blue-700 dark:text-blue-400">
                        already changed
                      </div>
                    </div>
                  )}
                  <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2">
                    <div className="text-lg font-semibold text-green-700 dark:text-green-300">
                      {eligible.length}
                    </div>
                    <div className="text-xs text-green-700 dark:text-green-400">will be changed</div>
                  </div>
                  <div className="rounded border border-slate-300 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/30 px-3 py-2">
                    <div className="text-lg font-semibold text-slate-600 dark:text-gray-300">
                      {skipped.length}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-gray-400">cannot be changed</div>
                  </div>
                </div>

                {/* Skipped pros + reasons */}
                {skipped.length > 0 && (
                  <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                    <table className="w-full text-[0.75rem]">
                      <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                        <tr>
                          <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                          <th className="text-left font-medium px-2 py-1.5">Cannot change — reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((r) => (
                          <tr key={String(r.pronumber)} className="border-t border-slate-100 dark:border-gray-700">
                            <td className="px-2 py-1 tabular-nums text-slate-700 dark:text-gray-200">{String(r.pronumber)}</td>
                            <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Eligible pros with non-blocking warnings (e.g. a mismatched line skipped) */}
                {warned.length > 0 && (
                  <div className="min-h-0 overflow-auto border border-amber-300 dark:border-amber-700 rounded">
                    <table className="w-full text-[0.75rem]">
                      <thead className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                        <tr>
                          <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                          <th className="text-left font-medium px-2 py-1.5">Will change — warning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {warned.map((r) => (
                          <tr key={String(r.pronumber)} className="border-t border-amber-100 dark:border-amber-800/50">
                            <td className="px-2 py-1 tabular-nums text-slate-700 dark:text-gray-200">{String(r.pronumber)}</td>
                            <td className="px-2 py-1 text-amber-700 dark:text-amber-300">{(r.warnings ?? []).join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {applyError && (
                  <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                    {applyError}
                  </div>
                )}
              </>
            )}

            {applying && progress && (
              <div className="shrink-0">
                <div className="flex items-center justify-between text-xs text-slate-600 dark:text-gray-300 mb-1">
                  <span>Applying changes to {plural(0)}…</span>
                  <span className="tabular-nums">{progress.done} of {progress.total}</span>
                </div>
                <div className="h-2 rounded bg-slate-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                  />
                </div>
                <div className="mt-1 text-[0.6875rem] text-slate-400 dark:text-gray-500">
                  Please keep this window open until it finishes.
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1 shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={applying}
                className="px-3 py-1.5 rounded border border-slate-300 dark:border-gray-600 text-sm hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applying || previewLoading || !!previewError || eligible.length === 0}
                title={eligible.length === 0
                  ? `No eligible ${plural(0)} to change` : 'Apply the changes'}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying
                  ? (progress ? `Applying… ${progress.done}/${progress.total}` : 'Applying…')
                  : `Apply to ${eligible.length} ${plural(eligible.length)}`}
              </button>
            </div>
          </div>
        )}

        {step === 'results' && (
          <div className="p-4 flex flex-col gap-3 min-h-0 overflow-auto">
            <div className="text-sm text-slate-700 dark:text-gray-200 shrink-0">
              Changes applied.
            </div>
            {applyError && (
              <div className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 shrink-0">
                The run stopped early: {applyError} — some pros below may not have been processed.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 shrink-0">
              <div className="rounded border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-300">
                  {applied.length} changed
                </span>
              </div>
              <div className="rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {notApplied.length} skipped
                </span>
              </div>
            </div>

            {applied.length > 0 && (
              <div className="text-xs text-slate-600 dark:text-gray-300 shrink-0">
                <span className="font-medium">Changed:</span>{' '}
                <span className="tabular-nums">{applied.map((r) => String(r.pronumber)).join(', ')}</span>
              </div>
            )}

            {appliedWithCaveats.length > 0 && (
              <div className="shrink-0 border border-amber-300 dark:border-amber-800 rounded overflow-auto">
                <table className="w-full text-[0.75rem]">
                  <thead className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                    <tr>
                      <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                      <th className="text-left font-medium px-2 py-1.5">Changed — with caveats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appliedWithCaveats.map((r) => (
                      <tr key={String(r.pronumber)} className="border-t border-amber-200 dark:border-amber-900">
                        <td className="px-2 py-1.5 tabular-nums align-top">{String(r.pronumber)}</td>
                        <td className="px-2 py-1.5 text-amber-800 dark:text-amber-300">
                          {(r.warnings ?? []).join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {notApplied.length > 0 && (
              <div className="min-h-0 overflow-auto border border-slate-200 dark:border-gray-700 rounded">
                <table className="w-full text-[0.75rem]">
                  <thead className="bg-slate-100 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300">
                    <tr>
                      <th className="text-left font-medium px-2 py-1.5 w-28">Pro</th>
                      <th className="text-left font-medium px-2 py-1.5">Skipped — reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notApplied.map((r) => (
                      <tr key={String(r.pronumber)} className="border-t border-slate-100 dark:border-gray-700">
                        <td className="px-2 py-1 tabular-nums text-slate-700 dark:text-gray-200">{String(r.pronumber)}</td>
                        <td className="px-2 py-1 text-slate-500 dark:text-gray-400">{r.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end pt-1 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ChangeAllReviewModal;
